import { useState, useEffect, useCallback, useMemo } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  Search, 
  Loader2, 
  Image as ImageIcon, 
  Shapes,
  X,
  Plus,
  Layers,
  Grid3X3,
  BookOpen,
  Tag,
  Frame,
  Type,
  Layout,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Star,
  FileText,
  Table,
  CheckCircle,
  Square,
  Circle,
  Triangle,
  Mic,
  Volume2,
  Filter
} from "lucide-react";

/**
 * LibraryPanel - Panneau de bibliothèque pédagogique persistant
 * Remplace les modals ClipArt et Templates par un panneau latéral gauche
 */

// ARASAAC API
const ARASAAC_API = "https://api.arasaac.org/v1";

// Catégories de cliparts pédagogiques
const CLIPART_CATEGORIES = [
  { id: "lecture", label: "Lecture", icon: "📖", color: "#3b82f6", keywords: ["lire", "lettre", "mot", "phrase", "son", "syllabe"] },
  { id: "maths", label: "Maths", icon: "🔢", color: "#10b981", keywords: ["nombre", "calcul", "addition", "géométrie", "mesure"] },
  { id: "consignes", label: "Consignes", icon: "📋", color: "#f59e0b", keywords: ["écouter", "écrire", "colorier", "découper", "coller"] },
  { id: "sciences", label: "Sciences", icon: "🔬", color: "#8b5cf6", keywords: ["observer", "expérience", "vivant", "corps", "planète"] },
  { id: "school", label: "École", icon: "📚", color: "#ef4444", keywords: ["classe", "professeur", "élève", "crayon", "bureau"] },
  { id: "animals", label: "Animaux", icon: "🐾", color: "#6b7280", keywords: ["animal", "chien", "chat", "oiseau", "poisson"] },
  { id: "people", label: "Personnes", icon: "👨‍👩‍👧", color: "#6b7280", keywords: ["garçon", "fille", "homme", "femme", "famille"] },
  { id: "emotions", label: "Émotions", icon: "😊", color: "#6b7280", keywords: ["content", "triste", "colère", "peur"] },
  { id: "nature", label: "Nature", icon: "🌳", color: "#6b7280", keywords: ["arbre", "fleur", "soleil", "pluie"] },
  { id: "objects", label: "Objets", icon: "📦", color: "#6b7280", keywords: ["table", "chaise", "livre", "stylo"] },
];

// Blocs pédagogiques prédéfinis
const PEDAGOGIC_BLOCKS = [
  // Étiquettes
  { id: "label-blue", name: "Étiquette bleue", category: "labels", type: "label", style: { width: 120, height: 40, fill: "#dbeafe", stroke: "#3b82f6", radius: 8 } },
  { id: "label-green", name: "Étiquette verte", category: "labels", type: "label", style: { width: 120, height: 40, fill: "#dcfce7", stroke: "#10b981", radius: 8 } },
  { id: "label-orange", name: "Étiquette orange", category: "labels", type: "label", style: { width: 120, height: 40, fill: "#fef3c7", stroke: "#f59e0b", radius: 8 } },
  { id: "label-purple", name: "Étiquette violette", category: "labels", type: "label", style: { width: 120, height: 40, fill: "#ede9fe", stroke: "#8b5cf6", radius: 8 } },
  { id: "label-dotted", name: "Étiquette pointillée", category: "labels", type: "label", style: { width: 120, height: 40, fill: "#ffffff", stroke: "#94a3b8", radius: 8, dash: true } },
  // Cadres
  { id: "frame-simple", name: "Cadre simple", category: "frames", type: "frame", style: { width: 200, height: 150, fill: "#ffffff", stroke: "#1e293b", radius: 0 } },
  { id: "frame-rounded", name: "Cadre arrondi", category: "frames", type: "frame", style: { width: 200, height: 150, fill: "#ffffff", stroke: "#3b82f6", radius: 16 } },
  { id: "frame-dotted", name: "Cadre pointillé", category: "frames", type: "frame", style: { width: 200, height: 150, fill: "#ffffff", stroke: "#64748b", radius: 8, dash: true } },
  { id: "frame-double", name: "Cadre double", category: "frames", type: "frame", style: { width: 200, height: 150, fill: "#fef3c7", stroke: "#f59e0b", radius: 4, double: true } },
  // Zones de réponse
  { id: "answer-line", name: "Ligne de réponse", category: "answers", type: "line", style: { width: 150, height: 30 } },
  { id: "answer-box", name: "Case réponse", category: "answers", type: "box", style: { width: 80, height: 40, fill: "#f8fafc", stroke: "#cbd5e1" } },
  { id: "answer-circle", name: "Cercle réponse", category: "answers", type: "circle", style: { radius: 20, fill: "#ffffff", stroke: "#3b82f6" } },
  // Indicateurs
  { id: "star-reward", name: "Étoile récompense", category: "indicators", type: "star", style: { fill: "#fbbf24", size: 40 } },
  { id: "check-success", name: "Check succès", category: "indicators", type: "check", style: { fill: "#10b981", size: 30 } },
  { id: "number-badge", name: "Badge numéro", category: "indicators", type: "badge", style: { fill: "#3b82f6", size: 36 } },
];

// Modèles de fiches
const TEMPLATES = [
  { id: "flashcard", name: "Flashcard", icon: "🃏", description: "Carte question/réponse" },
  { id: "quiz", name: "Quiz", icon: "❓", description: "Questions à choix" },
  { id: "etiquettes", name: "Étiquettes", icon: "🏷️", description: "Mots à manipuler" },
  { id: "carte-pince", name: "Carte à pince", icon: "📌", description: "Exercice autocorrectif" },
  { id: "dominos", name: "Dominos", icon: "🁠", description: "Jeu d'association" },
  { id: "fiche-ecriture", name: "Fiche écriture", icon: "✍️", description: "Support Seyes" },
  { id: "fiche-math", name: "Fiche math", icon: "🧮", description: "Opérations posées" },
];

// Block categories for filtering
const BLOCK_CATEGORIES = [
  { id: "all", label: "Tout", icon: Grid3X3 },
  { id: "labels", label: "Étiquettes", icon: Tag },
  { id: "frames", label: "Cadres", icon: Frame },
  { id: "answers", label: "Réponses", icon: Type },
  { id: "indicators", label: "Indicateurs", icon: Star },
];

export function LibraryPanel({ 
  isOpen, 
  onClose, 
  onSelectClipArt, 
  onSelectBlock, 
  onSelectTemplate,
  onGenerateAI 
}) {
  const [activeTab, setActiveTab] = useState("cliparts");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [blockCategory, setBlockCategory] = useState("all");
  const [cliparts, setCliparts] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Search cliparts from ARASAAC
  const searchCliparts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCliparts([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(
        `${ARASAAC_API}/pictograms/fr/search/${encodeURIComponent(query)}`
      );
      if (response.ok) {
        const data = await response.json();
        const results = data.slice(0, 30).map(item => ({
          id: item._id,
          name: item.keywords?.[0]?.keyword || "Image",
          url: `https://static.arasaac.org/pictograms/${item._id}/${item._id}_500.png`,
          keywords: item.keywords?.map(k => k.keyword) || [],
        }));
        setCliparts(results);
      }
    } catch (err) {
      console.error("ARASAAC search error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search when query changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchCliparts(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchCliparts]);

  // Search by category
  const searchByCategory = useCallback((category) => {
    setSelectedCategory(category.id);
    const keyword = category.keywords[0];
    setSearchQuery(keyword);
    searchCliparts(keyword);
  }, [searchCliparts]);

  // Filtered blocks
  const filteredBlocks = useMemo(() => {
    if (blockCategory === "all") return PEDAGOGIC_BLOCKS;
    return PEDAGOGIC_BLOCKS.filter(b => b.category === blockCategory);
  }, [blockCategory]);

  if (!isOpen) return null;

  return (
    <div className="w-72 bg-white border-r border-slate-200 flex flex-col h-full shadow-lg z-20">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
        <h2 className="font-bold text-slate-800 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-indigo-600" />
          Bibliothèque
        </h2>
        <button 
          onClick={onClose}
          className="p-1.5 hover:bg-white/50 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-slate-500" />
        </button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="grid grid-cols-3 mx-3 mt-3">
          <TabsTrigger value="cliparts" className="text-xs">
            <ImageIcon className="w-4 h-4 mr-1" />
            Cliparts
          </TabsTrigger>
          <TabsTrigger value="blocks" className="text-xs">
            <Shapes className="w-4 h-4 mr-1" />
            Blocs
          </TabsTrigger>
          <TabsTrigger value="templates" className="text-xs">
            <Layers className="w-4 h-4 mr-1" />
            Modèles
          </TabsTrigger>
        </TabsList>

        {/* Cliparts Tab */}
        <TabsContent value="cliparts" className="flex-1 flex flex-col p-3 pt-2">
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un clipart..."
              className="pl-9 h-9 text-sm"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500 animate-spin" />
            )}
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {CLIPART_CATEGORIES.slice(0, 5).map((cat) => (
              <button
                key={cat.id}
                onClick={() => searchByCategory(cat)}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedCategory === cat.id
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>

          {/* Results */}
          <ScrollArea className="flex-1">
            {cliparts.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {cliparts.map((clipart) => (
                  <button
                    key={clipart.id}
                    onClick={() => onSelectClipArt(clipart)}
                    className="aspect-square rounded-lg border-2 border-slate-200 hover:border-indigo-400 p-1 transition-all hover:shadow-md bg-white"
                    title={clipart.name}
                  >
                    <img
                      src={clipart.url}
                      alt={clipart.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            ) : !loading && searchQuery ? (
              <div className="text-center py-8 text-slate-500 text-sm">
                Aucun résultat pour "{searchQuery}"
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400 text-sm">
                <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                Recherchez un clipart ou<br />sélectionnez une catégorie
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Blocks Tab */}
        <TabsContent value="blocks" className="flex-1 flex flex-col p-3 pt-2">
          {/* Block category filter */}
          <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
            {BLOCK_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setBlockCategory(cat.id)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                  blockCategory === cat.id
                    ? "bg-indigo-500 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <cat.icon className="w-3.5 h-3.5" />
                {cat.label}
              </button>
            ))}
          </div>

          {/* Blocks grid */}
          <ScrollArea className="flex-1">
            <div className="grid grid-cols-2 gap-2">
              {filteredBlocks.map((block) => (
                <button
                  key={block.id}
                  onClick={() => onSelectBlock(block)}
                  className="p-3 rounded-xl border-2 border-slate-200 hover:border-indigo-400 transition-all hover:shadow-md bg-white text-left"
                >
                  {/* Block preview */}
                  <div 
                    className="w-full h-10 rounded-lg mb-2 flex items-center justify-center"
                    style={{ 
                      backgroundColor: block.style.fill,
                      border: `2px ${block.style.dash ? 'dashed' : 'solid'} ${block.style.stroke}`,
                      borderRadius: block.style.radius || 0
                    }}
                  >
                    {block.type === "star" && <Star className="w-5 h-5 text-amber-500" />}
                    {block.type === "check" && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                    {block.type === "badge" && <span className="text-white font-bold text-sm">1</span>}
                  </div>
                  <p className="text-xs font-medium text-slate-700 truncate">{block.name}</p>
                </button>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="flex-1 flex flex-col p-3 pt-2">
          <ScrollArea className="flex-1">
            <div className="space-y-2">
              {TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => onSelectTemplate(template)}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 hover:border-indigo-400 transition-all hover:shadow-md bg-white text-left flex items-center gap-3"
                >
                  <span className="text-2xl">{template.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800">{template.name}</p>
                    <p className="text-xs text-slate-500 truncate">{template.description}</p>
                  </div>
                  <Plus className="w-5 h-5 text-slate-400" />
                </button>
              ))}
            </div>

            {/* AI suggestion */}
            <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <span className="font-medium text-purple-800">Générer avec l'IA</span>
              </div>
              <p className="text-xs text-purple-600 mb-3">
                Décrivez votre fiche et l'IA vous propose une structure
              </p>
              <Button 
                onClick={onGenerateAI}
                size="sm" 
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Assistant IA
              </Button>
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default LibraryPanel;
